import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Joyride, STATUS } from "react-joyride";
import useCurrentMembership from "../helpers/useCurrentMembership";
import { TOURS } from "./tours";

const TourContext = createContext(null);

const STORAGE_PREFIX = "kronaly_tour_done_";

function storageKey(userId, tourKey) {
  return `${STORAGE_PREFIX}${userId || "anon"}_${tourKey}`;
}

function isDone(userId, tourKey) {
  try {
    return localStorage.getItem(storageKey(userId, tourKey)) === "1";
  } catch {
    return false;
  }
}

function markDone(userId, tourKey) {
  try {
    localStorage.setItem(storageKey(userId, tourKey), "1");
  } catch {
    /* localStorage no disponible (modo privado, embebido…) */
  }
}

/**
 * Provider global de tours. Renderiza un único Joyride en modo
 * uncontrolled — Joyride avanza internamente cuando se pulsa "Siguiente";
 * solo escuchamos `onEvent` para detectar fin/salto y marcar completado.
 *
 * Notas API react-joyride v3 (cambia respecto a v2):
 *  - `callback` → `onEvent(data, controls)`
 *  - `showProgress`, `skipScroll`, colores, zIndex → dentro de `options`
 *  - Sin `stepIndex`, Joyride lleva el estado internamente (uncontrolled)
 *  - Botones de UI vía `options.buttons: ['back','skip','primary']`
 */
export function TourProvider({ children }) {
  const { me } = useCurrentMembership();
  const userId = me?.id || null;

  const [activeTour, setActiveTour] = useState(null);
  // Token para forzar remount del componente Joyride en uncontrolled mode
  // (un replay del mismo tour no se distingue de "ya activo" sin esto).
  const [runToken, setRunToken] = useState(0);

  const startTour = useCallback(
    (key, opts = {}) => {
      const { force = false } = opts;
      if (!key || !TOURS[key]) return;
      if (activeTour) return; // un tour a la vez
      if (!force && isDone(userId, key)) return;
      setActiveTour(key);
      setRunToken((t) => t + 1);
    },
    [activeTour, userId],
  );

  const replayTour = useCallback(
    (key) => {
      // Si hay tour activo lo cerramos primero para garantizar remount limpio.
      if (activeTour) {
        setActiveTour(null);
        setTimeout(() => {
          setActiveTour(key);
          setRunToken((t) => t + 1);
        }, 0);
        return;
      }
      setActiveTour(key);
      setRunToken((t) => t + 1);
    },
    [activeTour],
  );

  const stop = useCallback(() => {
    setActiveTour(null);
  }, []);

  const handleEvent = useCallback(
    (data /* , controls */) => {
      const { status } = data || {};
      if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
        if (activeTour) markDone(userId, activeTour);
        stop();
      }
    },
    [activeTour, userId, stop],
  );

  const steps = useMemo(
    () => (activeTour ? TOURS[activeTour] : []),
    [activeTour],
  );

  const value = useMemo(
    () => ({ activeTour, startTour, replayTour, stop }),
    [activeTour, startTour, replayTour, stop],
  );

  return (
    <TourContext.Provider value={value}>
      {children}
      {activeTour && (
        <Joyride
          // Clave que cambia al replay para forzar remount.
          key={`${activeTour}-${runToken}`}
          steps={steps}
          run
          continuous
          scrollToFirstStep
          onEvent={handleEvent}
          locale={{
            back: "Atrás",
            close: "Cerrar",
            last: "¡Listo!",
            next: "Siguiente",
            open: "Abrir tour",
            skip: "Saltar",
          }}
          options={{
            primaryColor: "#3dc53a",
            backgroundColor: "#ffffff",
            arrowColor: "#ffffff",
            textColor: "#111827",
            overlayColor: "rgba(15,19,22,0.55)",
            zIndex: 10000,
            showProgress: true,
            // Botones visibles. Por defecto es ['back','close','primary'];
            // añadimos 'skip' para que el usuario pueda abortar el tour
            // completo (no solo cerrar un paso).
            buttons: ["back", "skip", "primary"],
            width: 380,
          }}
          // Joyride v3 sin estos overrides deja el contenido pegado al
          // borde del tooltip — sobre todo el footer con los botones.
          // Aplicamos el espaciado del DS para que respire.
          styles={{
            tooltipContainer: {
              padding: "18px 20px 12px",
            },
            tooltipContent: {
              padding: 0,
              fontSize: 14,
              lineHeight: 1.5,
              color: "#111827",
            },
            tooltipTitle: {
              fontSize: 16,
              fontWeight: 600,
              margin: "0 0 8px",
              color: "#111827",
            },
            tooltipFooter: {
              marginTop: 16,
              paddingTop: 12,
              borderTop: "1px solid rgba(0,0,0,0.06)",
            },
            buttonPrimary: {
              backgroundColor: "#3dc53a",
              borderRadius: 6,
              padding: "8px 14px",
              fontSize: 13,
              fontWeight: 600,
            },
            buttonBack: {
              color: "#6b7280",
              fontSize: 13,
              marginRight: 6,
            },
            buttonSkip: {
              color: "#6b7280",
              fontSize: 13,
            },
          }}
        />
      )}
    </TourContext.Provider>
  );
}

export function useTour() {
  const ctx = useContext(TourContext);
  if (!ctx) {
    return {
      activeTour: null,
      startTour: () => {},
      replayTour: () => {},
      stop: () => {},
    };
  }
  return ctx;
}

/**
 * Hook de conveniencia: cuando se monta el componente (= se entra en
 * esa página), arranca el tour si el usuario aún no lo ha completado.
 * Pasa false en `enabled` para suprimir (p. ej. mientras carga data
 * necesaria para que los anchors existan).
 */
export function useStartTour(key, { enabled = true } = {}) {
  const { startTour } = useTour();
  useEffect(() => {
    if (!enabled || !key) return;
    startTour(key);
  }, [key, enabled, startTour]);
}
